import { CSV_IMPORT_MAX_ROWS } from '../../../shared/csv-core/domain/csvConstants'
import { parseCsvText, rowToRecord } from '../../../shared/csv-core/infra/parseCsv';
import type { CatalogCsvEntityKind, CsvImportPreviewResult } from '../../domain/csv/csvEntityKinds';
import { countAllCatalogProducts } from '../catalogReadRepo';
import {
  logPreviewErrorSummary,
  previewRow,
  requireHeaders,
  type ParsedRow,
} from './catalogCsvImportHelpers';
import {
  loadCategoryCsvPreviewContext,
  previewCategoryCsvRow,
} from './catalogCsvImportCategories';
import { loadBrandCsvPreviewContext, previewBrandCsvRow } from './catalogCsvImportBrands';
import { loadSupplierCsvPreviewContext, previewSupplierCsvRow } from './catalogCsvImportSuppliers';
import {
  loadProductCsvPreviewContext,
  previewProductCsvRow,
} from './catalogCsvImportProductsPreview';

export async function previewCatalogCsvImport(
  entity: CatalogCsvEntityKind,
  csvText: string,
  opts?: { maxProducts?: number | null }
): Promise<CsvImportPreviewResult> {
  const parsed = parseCsvText(csvText);
  const headerError = requireHeaders(parsed.headers, entity);
  if (headerError) {
    return {
      entity,
      totalRows: 0,
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      rows: [previewRow(1, 'error', 'encabezado', headerError, headerError)],
      blocked: true,
      blockMessage: headerError,
    };
  }

  if (parsed.rows.length > CSV_IMPORT_MAX_ROWS) {
    const msg = `El archivo tiene más de ${CSV_IMPORT_MAX_ROWS} filas. Dividilo en partes más chicas.`;
    return {
      entity,
      totalRows: parsed.rows.length,
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      rows: [previewRow(1, 'error', 'límite', msg, msg)],
      blocked: true,
      blockMessage: msg,
    };
  }

  const rows: CsvImportPreviewResult['rows'] = [];
  const errorReasons = new Map<string, number>();
  let okCount = 0;
  let skipCount = 0;
  let errorCount = 0;
  let newProductCount = 0;

  const trackError = (message: string) => {
    const key = message.trim() || 'Error desconocido';
    errorReasons.set(key, (errorReasons.get(key) ?? 0) + 1);
  };

  const categoryPreviewCtx =
    entity === 'categories' ? await loadCategoryCsvPreviewContext() : null;
  const brandPreviewCtx = entity === 'brands' ? await loadBrandCsvPreviewContext() : null;
  const supplierPreviewCtx = entity === 'suppliers' ? await loadSupplierCsvPreviewContext() : null;
  const productPreviewCtx = entity === 'products' ? await loadProductCsvPreviewContext() : null;

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const rowNumber = i + 2;
    const record = rowToRecord(parsed.headers, parsed.rows[i]) as ParsedRow;

    try {
      if (entity === 'categories' && categoryPreviewCtx) {
        const outcome = previewCategoryCsvRow(rowNumber, record, categoryPreviewCtx);
        rows.push(outcome.row);
        okCount += outcome.okCount;
        skipCount += outcome.skipCount;
        errorCount += outcome.errorCount;
        continue;
      }

      if (entity === 'brands' && brandPreviewCtx) {
        const outcome = previewBrandCsvRow(rowNumber, record, brandPreviewCtx);
        rows.push(outcome.row);
        okCount += outcome.okCount;
        skipCount += outcome.skipCount;
        errorCount += outcome.errorCount;
        continue;
      }

      if (entity === 'suppliers' && supplierPreviewCtx) {
        const outcome = previewSupplierCsvRow(rowNumber, record, supplierPreviewCtx);
        rows.push(outcome.row);
        okCount += outcome.okCount;
        skipCount += outcome.skipCount;
        errorCount += outcome.errorCount;
        continue;
      }

      if (entity === 'products' && productPreviewCtx) {
        const outcome = await previewProductCsvRow(rowNumber, record, productPreviewCtx);
        rows.push(outcome.row);
        okCount += outcome.okCount;
        skipCount += outcome.skipCount;
        errorCount += outcome.errorCount;
        newProductCount += outcome.newProductCount;
        if (outcome.trackErrorMessage) {
          trackError(outcome.trackErrorMessage);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al validar fila.';
      trackError(msg);
      rows.push(previewRow(rowNumber, 'error', 'validar', '', msg));
      errorCount += 1;
    }
  }

  logPreviewErrorSummary(entity, errorReasons);

  let blocked = false;
  let blockMessage: string | undefined;
  if (entity === 'products' && opts?.maxProducts != null && newProductCount > 0) {
    const current = await countAllCatalogProducts();
    if (current + newProductCount > opts.maxProducts) {
      blocked = true;
      blockMessage = `Tu plan permite hasta ${opts.maxProducts} productos activos. Hoy tenés ${current} y el archivo agregaría ${newProductCount} nuevos. Sacá filas o actualizá el plan antes de importar.`;
    }
  }

  return {
    entity,
    totalRows: parsed.rows.length,
    okCount,
    skipCount,
    errorCount,
    rows,
    blocked,
    blockMessage,
  };
}
